import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  Autocomplete,
  TextField,
  Chip,
  CircularProgress,
  Paper,
  FormControl,
  OutlinedInput,
  InputAdornment,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import {
  Close as CloseIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { MaterialReactTable } from "material-react-table";
import { MRT_Localization_VI } from "material-react-table/locales/vi";
import { getToken } from "services/auth.service";
import { formatDate } from "utils/formatters";

const AddEventFromContractDialog = ({ open, onClose, onEventCreated }) => {
  const [step, setStep] = useState(0); // 0: select contract, 1: select items
  const [contracts, setContracts] = useState([]);
  const [selectedContract, setSelectedContract] = useState(null);
  const [contractItems, setContractItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Contract table states
  const [contractPagination, setContractPagination] = useState({
    pageIndex: 0,
    pageSize: 5,
  });
  const [contractTotalCount, setContractTotalCount] = useState(0);
  const [contractGlobalFilter, setContractGlobalFilter] = useState("");
  const [contractSorting, setContractSorting] = useState([]);
  const [contractLoading, setContractLoading] = useState(false);

  // Items states
  const [itemsGlobalFilter, setItemsGlobalFilter] = useState("");
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemsRowSelection, setItemsRowSelection] = useState({});

  // Status color function
  const getStatusColor = (status) => {
    const statusLower = status ? status.toLowerCase() : "";
    const colors = {
      "chưa bắt đầu": "#ffd700",
      "đang thực hiện": "#4caf50",
      "hoàn thành": "#2196f3",
      "tạm dừng": "#ff9800",
      "hủy bỏ": "#f44336",
    };
    return colors[statusLower] || "#9e9e9e";
  };

  // Helper function to check if item can create event
  const canCreateEvent = (item) => {
    // Can only create event if item has at least StartDate or EndDate
    return item.startDate || item.endDate;
  };

  // Contract table columns
  const contractColumns = [
    {
      accessorKey: "contractNumber",
      header: "Số hợp đồng",
      size: 140,
    },
    {
      accessorKey: "contractName",
      header: "Tên hợp đồng",
      size: 320,
      muiTableBodyCellProps: {
        sx: {
          whiteSpace: "normal",
          wordWrap: "break-word",
          lineHeight: 1.4,
        },
      },
    },
    {
      accessorKey: "signDate",
      header: "Ngày ký",
      size: 100,
      Cell: ({ cell }) => {
        const date = cell.getValue();
        return (
          <Typography
            variant="body2"
            sx={{ fontFamily: "Inter", fontSize: "0.875rem" }}
          >
            {/* {date ? new Date(date).toLocaleDateString("vi-VN") : "-"} */}
            {date ? formatDate(date) : "-"}
          </Typography>
        );
      },
    },
    {
      accessorKey: "status",
      header: "Trạng thái",
      size: 120,
      Cell: ({ cell }) => {
        const status = cell.getValue();
        return (
          <Chip
            label={status || "Chưa xác định"}
            size="small"
            sx={{
              backgroundColor: getStatusColor(status),
              color: "white",
              fontWeight: 500,
              fontSize: "11px",
              height: "24px",
              fontFamily: "Inter",
            }}
          />
        );
      },
    },
  ];

  // Contract items table columns
  const itemsColumns = [
    {
      accessorKey: "itemCode",
      header: "Mã hạng mục",
      size: 120,
      Cell: ({ row }) => {
        const level = row.original.level || 0;
        return (
          <Box sx={{ pl: level * 2 }}>
            <Typography
              variant="body2"
              sx={{
                fontFamily: "Inter",
                fontWeight: level === 0 ? 600 : 400,
              }}
            >
              {row.original.itemCode}
            </Typography>
          </Box>
        );
      },
    },
    {
      accessorKey: "itemName",
      header: "Tên hạng mục",
      size: 350,
      Cell: ({ row }) => {
        const level = row.original.level || 0;
        const canCreate = canCreateEvent(row.original);

        return (
          <Box sx={{ pl: level * 2 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: level === 0 ? 600 : 400,
                fontFamily: "Inter",
                whiteSpace: "normal",
                wordWrap: "break-word",
                lineHeight: 1.4,
              }}
            >
              {row.original.itemName}
            </Typography>
            {!canCreate && (
              <Chip
                label="Thiếu ngày"
                size="small"
                color="error"
                sx={{
                  mt: 0.5,
                  fontSize: "0.65rem",
                  height: "18px",
                }}
              />
            )}
          </Box>
        );
      },
    },
    {
      accessorKey: "startDate",
      header: "Ngày bắt đầu",
      size: 120,
      Cell: ({ cell, row }) => {
        const item = row.original;

        // Chỉ hiển thị ngày cho leaf nodes (hạng mục con cuối cùng)
        if (item.children && item.children.length > 0) {
          return null;
        }

        return (
          <Typography
            variant="body2"
            sx={{ fontFamily: "Inter", fontSize: "0.75rem" }}
          >
            {/* {cell.getValue()
              ? new Date(cell.getValue()).toLocaleDateString("vi-VN")
              : "-"} */}
            {cell.getValue()
              ? formatDate(cell.getValue())
              : "-"}
          </Typography>
        );
      },
    },
    {
      accessorKey: "endDate",
      header: "Ngày kết thúc",
      size: 120,
      Cell: ({ cell, row }) => {
        const item = row.original;

        // Chỉ hiển thị ngày cho leaf nodes (hạng mục con cuối cùng)
        if (item.children && item.children.length > 0) {
          return null;
        }

        return (
          <Typography
            variant="body2"
            sx={{ fontFamily: "Inter", fontSize: "0.75rem" }}
          >
            {/* {cell.getValue()
              ? new Date(cell.getValue()).toLocaleDateString("vi-VN")
              : "-"} */}
            {cell.getValue()
              ? formatDate(cell.getValue())
              : "-"}
          </Typography>
        );
      },
    },
  ];

  // Handle contract selection from table
  const handleContractSelect = (contract) => {
    setSelectedContract(contract);
    setStep(1); // Move to items selection step
  };

  // Handle step navigation
  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const handleNext = () => {
    if (step === 0 && selectedContract) {
      setStep(1);
    }
  };

  // Fetch contracts with pagination
  const fetchContracts = useCallback(async () => {
    try {
      setContractLoading(true);
      setError("");

      const token = await getToken();
      if (!token) {
        setError("Phiên đăng nhập đã hết hạn");
        return;
      }

      const requestData = {
        excludedContractIds: [],
        pageNumber: contractPagination.pageIndex + 1,
        pageSize: contractPagination.pageSize,
        sortColumn:
          contractSorting.length > 0 ? contractSorting[0].id : "SignDate",
        sortDirection:
          contractSorting.length > 0
            ? contractSorting[0].desc
              ? "DESC"
              : "ASC"
            : "DESC",
      };

      if (contractGlobalFilter && contractGlobalFilter.trim()) {
        requestData.searchTerm = contractGlobalFilter.trim();
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/Contract/available-for-user`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestData),
        }
      );

      if (!response.ok) {
        throw new Error("Không thể tải danh sách hợp đồng");
      }

      const result = await response.json();

      if (result.success && result.data) {
        setContracts(result.data);
        setContractTotalCount(result.totalCount || 0);
      } else {
        setError(result.message || "Lỗi khi tải danh sách hợp đồng");
        setContracts([]);
        setContractTotalCount(0);
      }
    } catch (error) {
      console.error("Error fetching contracts:", error);
      setError("Lỗi khi tải danh sách hợp đồng");
      setContracts([]);
      setContractTotalCount(0);
    } finally {
      setContractLoading(false);
    }
  }, [contractPagination, contractSorting, contractGlobalFilter]);

  // Load users for sharing
  const fetchUsers = useCallback(async () => {
    try {
      const token = await getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/Events/users-for-sharing`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Không thể tải danh sách người dùng");
      }

      const data = await response.json();
      setUsers(data);
    } catch (error) {
      console.error("Error fetching users:", error);
      setError(error.message);
    }
  }, []);

  // Effects
  useEffect(() => {
    if (open) {
      fetchContracts();
      fetchUsers();
    }
  }, [open, fetchContracts, fetchUsers]);

  // Reset pagination when searching contracts
  useEffect(() => {
    if (open && contractGlobalFilter !== "") {
      setContractPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  }, [contractGlobalFilter, open]);

  // Fetch contracts when pagination/sorting changes
  useEffect(() => {
    if (open && step === 0) {
      fetchContracts();
    }
  }, [contractPagination, contractSorting, open, step, fetchContracts]);

  useEffect(() => {
    const fetchContractItems = async () => {
      setItemsLoading(true);
      try {
        const token = await getToken();
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/contract-items/by-contract/${selectedContract.contractId}/hierarchical`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Không thể tải danh sách hạng mục");
        }

        const data = await response.json();


        // Check if data is valid
        if (!data) {
          throw new Error("Không có dữ liệu hạng mục");
        }

        // Handle different response formats
        let itemsData = data;
        if (data.success && data.data) {
          itemsData = data.data;
        } else if (data.items) {
          itemsData = data.items;
        }

        // Ensure itemsData is an array
        if (!Array.isArray(itemsData)) {
          console.error("Items data is not an array:", itemsData);
          setContractItems([]);
          return;
        }

        // Sort items by ItemCode recursively (same as ContractItemsTable)
        const sortItemsByCode = (items) => {
          const parseItemCode = (code) => {
            if (!code) return [0];
            return code.split(".").map((part) => parseInt(part) || 0);
          };

          const compareItemCodes = (a, b) => {
            const codeA = parseItemCode(a.itemCode);
            const codeB = parseItemCode(b.itemCode);

            const maxLength = Math.max(codeA.length, codeB.length);

            for (let i = 0; i < maxLength; i++) {
              const numA = codeA[i] || 0;
              const numB = codeB[i] || 0;

              if (numA !== numB) {
                return numA - numB;
              }
            }
            return 0;
          };

          const sortedItems = items.sort(compareItemCodes);

          // Recursively sort children
          return sortedItems.map((item) => ({
            ...item,
            children: item.children ? sortItemsByCode(item.children) : [],
          }));
        };

        // Sort the hierarchical data first
        const sortedItemsData = sortItemsByCode(itemsData);

        // Flatten hierarchical data to flat list for display (same as ContractItemsTable)
        const flattenItems = (items, level = 0) => {
          let result = [];
          for (const item of items) {
            result.push({
              ...item,
              level: level,
              displayName:
                "  ".repeat(level) +
                (item.itemCode || "") +
                " - " +
                (item.itemName || ""),
            });
            if (item.children && item.children.length > 0) {
              result = result.concat(flattenItems(item.children, level + 1));
            }
          }
          return result;
        };

        const flatItems = flattenItems(sortedItemsData);
        setContractItems(flatItems);
      } catch (error) {
        console.error("Error fetching contract items:", error);
        setError(error.message);
      } finally {
        setItemsLoading(false);
      }
    };

    const fetchContractUsers = async () => {
      try {
        const token = await getToken();
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/Events/contract-users/${selectedContract.contractId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Không thể tải danh sách người dùng hợp đồng");
        }

        const data = await response.json();
        // Auto-select contract users
        setSelectedUsers((prev) => {
          const newUsers = data.filter(
            (user) => !prev.some((existing) => existing.userId === user.userId)
          );
          return [...prev, ...newUsers];
        });
      } catch (error) {
        console.error("Error fetching contract users:", error);
        // Don't show error for this as it's optional
      }
    };

    if (selectedContract) {
      fetchContractItems();
      fetchContractUsers();
    } else {
      setContractItems([]);
      setSelectedItems([]);
      setItemsRowSelection({});
    }
  }, [selectedContract]);

  const handleCreateEvents = async () => {
    if (!selectedContract || selectedItems.length === 0) {
      setError("Vui lòng chọn hợp đồng và ít nhất một hạng mục");
      return;
    }

    // Filter only valid items that can create events
    const validItems = selectedItems.filter(canCreateEvent);

    if (validItems.length === 0) {
      setError(
        "Không có hạng mục hợp lệ để tạo sự kiện. Vui lòng chọn hạng mục có StartDate hoặc EndDate."
      );
      return;
    }

    setLoading(true);
    setError("");

    try {
      const requestData = {
        contractId: selectedContract.contractId,
        itemIds: validItems.map((item) => item.itemId),
        sharedUserIds: selectedUsers.map((user) => user.userId),
      };

      const token = getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/Events/create-from-contract`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(requestData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Không thể tạo sự kiện từ hợp đồng"
        );
      }

      const result = await response.json();

      // Show success message with number of valid events created
      if (validItems.length < selectedItems.length) {
        setError(
          `Đã tạo ${validItems.length} sự kiện thành công. ${selectedItems.length - validItems.length
          } hạng mục bị bỏ qua do thiếu thông tin ngày.`
        );
        // Clear error after 3 seconds to show it as info
        setTimeout(() => setError(""), 3000);
      }

      // Notify parent component
      onEventCreated && onEventCreated(result);

      // Reset form
      setStep(0);
      setSelectedContract(null);
      setSelectedItems([]);
      setSelectedUsers([]);
      setContractItems([]);
      setItemsRowSelection({});

      onClose();
    } catch (error) {
      console.error("Error creating events:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(0);
    setSelectedContract(null);
    setSelectedItems([]);
    setSelectedUsers([]);
    setContractItems([]);
    setContractGlobalFilter("");
    setItemsGlobalFilter("");
    setItemsRowSelection({});
    setError("");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <BusinessIcon color="primary" />
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          Tạo sự kiện từ hợp đồng
        </Typography>
        <Button onClick={handleClose} size="small">
          <CloseIcon />
        </Button>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Stepper */}
        <Box sx={{ mb: 3 }}>
          <Stepper activeStep={step} alternativeLabel>
            <Step>
              <StepLabel>Chọn hợp đồng</StepLabel>
            </Step>
            <Step>
              <StepLabel>Chọn hạng mục</StepLabel>
            </Step>
          </Stepper>
        </Box>

        {/* Step 0: Contract Selection */}
        {step === 0 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontFamily: "Inter" }}>
              Chọn hợp đồng
            </Typography>

            {/* Search contract */}
            <Box sx={{ mb: 2 }}>
              <FormControl size="small" sx={{ minWidth: 300 }}>
                <OutlinedInput
                  placeholder="Tìm kiếm hợp đồng..."
                  value={contractGlobalFilter}
                  onChange={(e) => setContractGlobalFilter(e.target.value)}
                  startAdornment={
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  }
                  sx={{
                    fontFamily: "Inter",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e0e0e0",
                    },
                  }}
                />
              </FormControl>
            </Box>

            {/* Contract table */}
            <Paper sx={{ border: "1px solid #e0e0e0", boxShadow: "none" }}>
              <MaterialReactTable
                columns={contractColumns}
                data={contracts}
                enableColumnActions={false}
                enableColumnFilters={false}
                enablePagination={true}
                enableSorting={true}
                enableBottomToolbar={true}
                enableTopToolbar={false}
                manualPagination={true}
                manualSorting={true}
                muiTableBodyRowProps={({ row }) => ({
                  onClick: () => handleContractSelect(row.original),
                  sx: {
                    cursor: "pointer",
                    backgroundColor:
                      selectedContract?.contractId === row.original.contractId
                        ? "#f5f5f5"
                        : "inherit",
                    "&:hover": {
                      backgroundColor: "#f0f0f0",
                    },
                  },
                })}
                muiTablePaperProps={{
                  sx: {
                    boxShadow: "none",
                    border: "none",
                  },
                }}
                muiTableProps={{
                  sx: {
                    fontFamily: "Inter",
                  },
                }}
                muiTableHeadCellProps={{
                  sx: {
                    fontFamily: "Inter",
                    fontWeight: 600,
                    backgroundColor: "#f8f9fa",
                  },
                }}
                muiTableBodyCellProps={{
                  sx: {
                    fontFamily: "Inter",
                  },
                }}
                onPaginationChange={setContractPagination}
                onSortingChange={setContractSorting}
                rowCount={contractTotalCount}
                state={{
                  isLoading: contractLoading,
                  pagination: contractPagination,
                  sorting: contractSorting,
                }}
                initialState={{
                  density: "compact",
                  sorting: [{ id: "signDate", desc: true }],
                }}
                muiCircularProgressProps={{
                  color: "primary",
                  thickness: 5,
                  size: 30,
                }}
                localization={MRT_Localization_VI}
              />
            </Paper>

            {selectedContract && (
              <Alert severity="success" sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ fontFamily: "Inter" }}>
                  Đã chọn: <strong>{selectedContract.contractNumber}</strong> -{" "}
                  {selectedContract.contractName}
                </Typography>
              </Alert>
            )}
          </Box>
        )}

        {/* Step 1: Items Selection */}
        {step === 1 && selectedContract && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontFamily: "Inter" }}>
              Chọn hạng mục cho hợp đồng: {selectedContract.contractNumber}
            </Typography>

            {/* Search items */}
            <Box sx={{ mb: 2 }}>
              <FormControl size="small" sx={{ minWidth: 300 }}>
                <OutlinedInput
                  placeholder="Tìm kiếm hạng mục..."
                  value={itemsGlobalFilter}
                  onChange={(e) => setItemsGlobalFilter(e.target.value)}
                  startAdornment={
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  }
                  sx={{
                    fontFamily: "Inter",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e0e0e0",
                    },
                  }}
                />
              </FormControl>
            </Box>

            {/* Items table */}
            <Paper sx={{ border: "1px solid #e0e0e0", boxShadow: "none" }}>
              <MaterialReactTable
                columns={itemsColumns}
                data={
                  Array.isArray(contractItems)
                    ? contractItems.filter((item) => {
                      if (!itemsGlobalFilter) return true;
                      const searchTerm = itemsGlobalFilter.toLowerCase();
                      return (
                        (item.itemCode || "")
                          .toLowerCase()
                          .includes(searchTerm) ||
                        (item.itemName || "")
                          .toLowerCase()
                          .includes(searchTerm)
                      );
                    })
                    : []
                }
                enableColumnActions={false}
                enableColumnFilters={false}
                enablePagination={false}
                enableSorting={false}
                enableBottomToolbar={false}
                enableTopToolbar={false}
                enableRowSelection={true}
                rowSelection={itemsRowSelection}
                onRowSelectionChange={(updater) => {
                  if (typeof updater === "function") {
                    const newSelection = updater(itemsRowSelection);
                    setItemsRowSelection(newSelection);

                    const selectedIds = Object.keys(newSelection);
                    const selectedItemsData = Array.isArray(contractItems)
                      ? contractItems.filter((item) =>
                        selectedIds.includes(item.itemId.toString())
                      )
                      : [];
                    setSelectedItems(selectedItemsData);
                  }
                }}
                muiTableBodyRowProps={({ row }) => {
                  const canCreate = canCreateEvent(row.original);
                  return {
                    sx: {
                      cursor: canCreate ? "pointer" : "not-allowed",
                      opacity: canCreate ? 1 : 0.5,
                      backgroundColor: canCreate ? "inherit" : "#fafafa",
                    },
                  };
                }}
                enableSelectAll={false}
                getRowId={(row) => row.itemId}
                muiSelectCheckboxProps={({ row }) => ({
                  disabled: !canCreateEvent(row.original),
                })}
                muiTablePaperProps={{
                  sx: {
                    boxShadow: "none",
                    border: "none",
                  },
                }}
                muiTableProps={{
                  sx: {
                    fontFamily: "Inter",
                  },
                }}
                muiTableHeadCellProps={{
                  sx: {
                    fontFamily: "Inter",
                    fontWeight: 600,
                    backgroundColor: "#f8f9fa",
                  },
                }}
                muiTableBodyCellProps={{
                  sx: {
                    fontFamily: "Inter",
                  },
                }}
                state={{
                  isLoading: itemsLoading,
                  rowSelection: Array.isArray(selectedItems)
                    ? selectedItems.reduce(
                      (acc, item) => ({
                        ...acc,
                        [item.itemId]: true,
                      }),
                      {}
                    )
                    : {},
                }}
                initialState={{
                  density: "compact",
                }}
                muiCircularProgressProps={{
                  color: "primary",
                  thickness: 5,
                  size: 30,
                }}
                localization={MRT_Localization_VI}
              />
            </Paper>

            {/* No items message */}
            {!itemsLoading &&
              Array.isArray(contractItems) &&
              contractItems.length === 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ fontFamily: "Inter" }}>
                    Hợp đồng này chưa có hạng mục nào.
                  </Typography>
                </Alert>
              )}

            {selectedItems.length > 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ fontFamily: "Inter" }}>
                  Đã chọn{" "}
                  <strong>{selectedItems.filter(canCreateEvent).length}</strong>{" "}
                  / {selectedItems.length} hạng mục hợp lệ
                  {selectedItems.filter((item) => !canCreateEvent(item))
                    .length > 0 && (
                      <Box
                        component="span"
                        sx={{ display: "block", mt: 1, color: "#ff9800" }}
                      >
                        <small>
                          {
                            selectedItems.filter((item) => !canCreateEvent(item))
                              .length
                          }{" "}
                          hạng mục thiếu thông tin ngày (StartDate hoặc EndDate)
                        </small>
                      </Box>
                    )}
                </Typography>
              </Alert>
            )}

            {/* User selection */}
            <Box sx={{ mt: 3 }}>
              <Autocomplete
                multiple
                options={users}
                getOptionLabel={(option) => option.fullName}
                value={selectedUsers}
                onChange={(event, newValue) => {
                  setSelectedUsers(newValue);
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option.fullName}
                      {...getTagProps({ index })}
                      key={option.userId}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Chia sẻ với người dùng"
                    placeholder="Chọn người dùng..."
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.userId}>
                    <Box>
                      <Typography variant="body2">{option.fullName}</Typography>
                      {option.email && (
                        <Typography variant="caption" color="text.secondary">
                          {option.email}
                        </Typography>
                      )}
                    </Box>
                  </li>
                )}
              />
            </Box>

            {/* Summary */}
            {selectedItems.length > 0 && (
              <Alert severity="info" icon={<CheckCircleIcon />} sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ fontFamily: "Inter" }}>
                  Sẽ tạo <strong>{selectedItems.length}</strong> sự kiện từ hợp
                  đồng <strong>{selectedContract?.contractNumber}</strong>
                  {selectedUsers.length > 0 && (
                    <>
                      {" "}
                      và chia sẻ với <strong>
                        {selectedUsers.length}
                      </strong>{" "}
                      người dùng
                    </>
                  )}
                </Typography>
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={handleClose}
          color="inherit"
          sx={{ textTransform: "none" }}
        >
          Hủy
        </Button>

        {step > 0 && (
          <Button onClick={handleBack} sx={{ mr: 1, textTransform: "none" }}>
            Quay lại
          </Button>
        )}

        {step === 0 && selectedContract && (
          <Button
            onClick={handleNext}
            variant="contained"
            sx={{ textTransform: "none" }}
          >
            Tiếp tục
          </Button>
        )}

        {step === 1 && (
          <Button
            onClick={handleCreateEvents}
            variant="contained"
            disabled={
              selectedItems.filter(canCreateEvent).length === 0 || loading
            }
            startIcon={
              loading ? <CircularProgress size={20} /> : <CheckCircleIcon />
            }
            sx={{ textTransform: "none" }}
          >
            {loading
              ? "Đang tạo..."
              : `Tạo ${selectedItems.filter(canCreateEvent).length} sự kiện`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AddEventFromContractDialog;
